import type { FC } from 'react'
import { useMemo } from 'react'
import type { Asset } from 'contentful'
import styled from '@emotion/styled'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import ButtonText from '@segment/matcha/components/ButtonText'
import Loader from '@segment/matcha/components/Loader'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import { useFetchGDBMDataQuery } from '@segment/api/routes/goodDataBetterMarketing/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Recent episodes`,
  loadMore: `Load more`,
}

const RecentEpisodes: FC = () => {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useFetchGDBMDataQuery({ args: [] })

  return (
    <SectionBase variant="noCopy">
      <Section.Contents>
        <StyledCopyTextBase centered={true}>
          <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
        </StyledCopyTextBase>
        <ColumnTrack
          data-testid="loading-overlay"
          aria-live="polite"
          aria-busy={isLoading || isFetchingNextPage}
          data-loading={isLoading || isFetchingNextPage}
          columns={1}
          overrides={{
            md: 2,
            xl: 3,
          }}
        >
          {data &&
            data.pages.map((page) =>
              page?.items.map(({ fields }) => {
                return (
                  <Episode
                    key={fields.slug}
                    title={fields.episodeTitle}
                    description={fields.episodeDescription}
                    slug={fields.slug}
                    image={fields.episodeImage}
                  />
                )
              })
            )}
        </ColumnTrack>
        {(isLoading || isFetchingNextPage) && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        {hasNextPage && (
          <LoadMoreWrapper>
            <ButtonText
              onClick={() => fetchNextPage()}
              disabled={isLoading || isFetchingNextPage}
              iconEnd={ChevronRightIcon}
            >
              {copy.loadMore}
            </ButtonText>
          </LoadMoreWrapper>
        )}
      </Section.Contents>
    </SectionBase>
  )
}

interface Episode {
  title: string
  description: string
  slug: string
  image: Asset
}

const Episode: FC<Episode> = ({ title, description, slug, image }) => {
  const pageConfig = usePageConfig()

  const data = useMemo(
    () => ({
      title,
      description,
      href: getAbsoluteUrl(`/good-data-better-marketing/${slug}/`, pageConfig),
      image,
    }),
    [title, description, pageConfig, slug, image]
  )
  return (
    <CardBase href={data.href} layout="vertical">
      <Card.Image
        variant="fill"
        alt={image.fields.title}
        src={image.fields.file.url}
        width={image.fields.file.details.image?.width}
        height={image.fields.file.details.image?.height}
      />
      <Card.Body>
        <Card.Copy>
          <Card.Headline tag="h3">{data.title}</Card.Headline>
          <Card.Description variant="pLarge">{data.description}</Card.Description>
        </Card.Copy>
        <Card.Action alignment="left">
          <ButtonText iconEnd={ChevronRightIcon}>Go to episode</ButtonText>
        </Card.Action>
      </Card.Body>
    </CardBase>
  )
}

const LoaderWrapper = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  margin: 0 auto;
`

const StyledCopyTextBase = styled(CopyTextBase)`
  padding-bottom: 56px;
`

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 56px;
`

export default RecentEpisodes
